import { CustomColumnTypes } from "@/enum/list.enum";

import { ColumnConfig } from "../../../../List/schema/columnConfig";
import { EventOutreachSchemaType, ListPerson } from "../../../../List/schema/list";
import {
	isNormalizedCompanyNameKey,
	resolveNormalizedCompanyName,
} from "../../../../List/components/CampaignOverview/utils/normalizedCompanyName";
import { getSuggestionLabel } from "../../../../List/components/CampaignOverview/utils/suggestions.utils";

/** Bucket a variable row belongs to: event-outreach tokens vs everything else. */
export type VariableCategory = "default" | "event";

/** Row model of the variables section. */
export interface VariableRow {
	/** Placeholder key, e.g. "prospect_firstName" or "column_2097_time". */
	key: string;
	/** Human label, e.g. "Prospect First Name". */
	label: string;
	/** Value resolved for the current lead; "" when none is available. */
	sampleValue: string;
	/** Whether the placeholder occurs in the selected node's messages. */
	isInUse: boolean;
	/** Which bucket the row renders in — event tokens get their own section. */
	category: VariableCategory;
}

/**
 * Fixed event-outreach token names (PREC-3520), identical across lists. Kept in
 * sync with `addEventSuggestions` (column-utils) and `getSuggestionLabel`.
 */
export const EVENT_VARIABLE_KEYS = new Set([
	"event_name",
	"event_start",
	"event_end",
	"meeting_location",
	"meeting_link",
	"meeting_time",
]);

/**
 * Whether a placeholder key is an event-outreach variable — either one of the
 * fixed tokens or a per-list custom event variable.
 */
export const isEventVariableKey = (
	key: string,
	listEvent?: EventOutreachSchemaType | null
): boolean => {
	if (EVENT_VARIABLE_KEYS.has(key)) {
		return true;
	}

	return Boolean(listEvent?.customVariables?.some((variable) => variable.key === key));
};

export const normalizeVariableSearch = (value?: string | null): string =>
	(value ?? "").trim().toLowerCase();

/** Builds the `{key|label}` token the editors render as a placeholder pill. */
export const buildVariableToken = (key: string, label?: string): string =>
	`{${key}|${label || key}}`;

interface NodeMessageFields {
	message?: string;
	subject?: string;
	premiumMessage?: string;
	alternateMessage?: string;
	alternateSubject?: string;
	alternatePremiumMessage?: string;
}

/** Collects every message-bearing field of a node's data, skipping empties. */
export const collectNodeMessageContents = (data?: NodeMessageFields): string[] =>
	[
		data?.message,
		data?.subject,
		data?.premiumMessage,
		data?.alternateMessage,
		data?.alternateSubject,
		data?.alternatePremiumMessage,
	].filter((content): content is string => Boolean(content));

/** Column types that get their own section card instead of a variables row. */
const SECTIONED_COLUMN_TYPES = new Set([
	CustomColumnTypes.AI_SNIPPET,
	CustomColumnTypes.RANDOM_VALUE,
]);

/**
 * Ids of columns rendered by their own section card — AI snippets and spintax
 * (RANDOM_VALUE) placeholders live in the AI snippets / spintax cards.
 */
const collectSectionedColumnIds = (columnLists: (ColumnConfig[] | undefined)[]): Set<number> => {
	const ids = new Set<number>();

	for (const columns of columnLists) {
		for (const column of columns ?? []) {
			if (SECTIONED_COLUMN_TYPES.has(column.type) && column.id != null) {
				ids.add(column.id);
			}
		}
	}

	return ids;
};

const COLUMN_KEY_REGEX = /^column_(\d+)_/;

/** Sender-side keys whose map values are labels, not lead data. */
const SENDER_KEYS = new Set(["senderFirstName", "senderLastName", "signature", "unsubscribe_link"]);

/** Whether a placeholder key resolves from the sender instead of the lead. */
export const isSenderVariableKey = (key: string): boolean => SENDER_KEYS.has(key);

interface BuildVariableRowsArgs {
	/** Flattened placeholder key → value map (see `getSuggestionsMap`). */
	suggestionsMap: Record<string, unknown>;
	currentPerson?: ListPerson | null;
	listColumns?: ColumnConfig[];
	listEvent?: EventOutreachSchemaType | null;
	/** Placeholder names extracted from the selected node's messages. */
	usedVarNames: string[];
	/** When false the map holds example fixtures — values are not shown. */
	hasRealLead: boolean;
}

/**
 * Maps the flattened suggestions of the current lead to section rows: drops
 * keys without a label (the `key|label` duplicates and unlabeled object
 * subkeys, mirroring the editors' autocomplete) and columns with their own
 * section card (AI snippets, spintax), and marks rows whose placeholder
 * occurs in the selected node's messages.
 */
export const buildVariableRows = ({
	suggestionsMap,
	currentPerson,
	listColumns,
	listEvent,
	usedVarNames,
	hasRealLead,
}: BuildVariableRowsArgs): VariableRow[] => {
	const usedSet = new Set(usedVarNames);
	const sectionedColumnIds = collectSectionedColumnIds([listColumns, currentPerson?.list?.columns]);

	const rows: VariableRow[] = [];

	for (const [key, value] of Object.entries(suggestionsMap)) {
		const columnMatch = COLUMN_KEY_REGEX.exec(key);

		if (columnMatch && sectionedColumnIds.has(Number(columnMatch[1]))) {
			continue;
		}

		const label: string | undefined = getSuggestionLabel(
			key,
			currentPerson ?? undefined,
			listColumns,
			listEvent
		);

		if (label === undefined) {
			continue;
		}

		const showValue = hasRealLead && !SENDER_KEYS.has(key) && value != null;

		// The normalized company name is null until something resolves it, so the row would show a
		// blank sample for a company that has a perfectly good name. Preview what the send would put
		// there instead (PREC-3923).
		const sampleValue =
			hasRealLead && isNormalizedCompanyNameKey(key)
				? resolveNormalizedCompanyName(value, currentPerson).trim()
				: showValue
					? String(value).trim()
					: "";

		rows.push({
			key,
			label: String(label),
			sampleValue,
			isInUse: usedSet.has(key),
			category: isEventVariableKey(key, listEvent) ? "event" : "default",
		});
	}

	return rows;
};
